// 外部ファイル読み込み
import { initScrambleAnimation } from './animation/textScrambleAnimation.js';

/* global gsap, ScrollTrigger */

(() => {
	// eslint-disable-next-line no-console
	// ============================================
	// 共通変数
	// ============================================
	//bodyタグ
	const body = document.body;
	const bodyClassList = body.classList;
	const html = document.documentElement;
	const htmlClassList = html.classList;
	//メディアクエリ
	const breakPointPc = 768;
	const mql = window.matchMedia(`(min-width: ${breakPointPc}px)`);
	//画面幅別のクラス名
	const widthPrefix = 'is-width';
	const pcWidthClass = `${widthPrefix}-pc`;
	const spWidthClass = `${widthPrefix}-sp`;
	//メニュー
	const dataToggleMenu = 'data-toggle-menu';
	const menuOpenClass = 'is-menu-open';
	const menuShowClass = 'is-menu-show';
	const hiddenClass = 'is-hidden';
	//setTimeout
	const setSecondsFast = 200;

	// ============================================
	// 共通処理
	// ============================================
	//クラス付け替え
	const toggleMenuClass = (firstClass, secondClass) => {
		//bodyに最初のクラス追加
		// bodyClassList.toggle(hiddenClass);
		bodyClassList.toggle(firstClass);
		//htmlに背景固定クラス追加
		htmlClassList.toggle(hiddenClass);

		setTimeout(() => {
			//bodyに2つ目のクラス追加
			bodyClassList.toggle(secondClass);
		}, setSecondsFast);
	};

	//rem計算
	const remToPx = (rem) => {
		return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
	};

	// ============================================
	// 画面幅に応じてclass付与
	// ============================================
	const addClassWindow = () => {
		const checkWindow = (windowSize) => {
			bodyClassList.toggle(pcWidthClass, windowSize.matches); // 768px以上の時（PCの処理）
			bodyClassList.toggle(spWidthClass, !windowSize.matches); // 768px以下の時（スマホの処理）
		};
		// ロード時に判定
		checkWindow(mql);
		// ブレイクポイントが切り替わったら判定
		mql.addEventListener('change', checkWindow);
	};

	// ============================================
	// スクロールでclass付与
	// ============================================
	const addClassScroll = () => {
		let done = false;

		const handleScroll = () => {
			bodyClassList.toggle('is-scroll', 0 < window.scrollY);
			done = false;
		};

		window.addEventListener('scroll', () => {
			if (!done) {
				done = true;
				requestAnimationFrame(handleScroll);
			}
		});
	};

	// ============================================
	// 背景スクロール固定（スマホメニュー＋スムーススクロール）
	// ============================================
	//メニューの開閉状態を管理するフラグ
	let isMenuOpen = false;

	// touchmove常時登録
	document.addEventListener(
		'touchmove',
		(e) => {
			// メニューが開いていなければ何もしない
			if (!isMenuOpen) return;

			// メニュー本体が取得できない場合は全面スクロール禁止
			const navBody = document.querySelector('[data-menu-body]');
			if (!navBody) {
				e.preventDefault();
				return;
			}

			// スクロール不要判定
			const nav = document.querySelector('[data-menu-wrap]');
			const navHead = document.querySelector('[data-menu-head]');
			if (!nav) {
				e.preventDefault();
				return;
			}

			// メニュー全体の高さ
			const navHeight = nav.getBoundingClientRect().height;

			// ヘッダー部分の高さ
			const headHeight = navHead ? navHead.getBoundingClientRect().height : 0;

			// 余白（4rem）を px に変換
			const marginPx = remToPx(4);

			// 実際に表示可能な高さ
			const availableHeight = navHeight - headHeight - marginPx;

			// 中身がすべて見えているか（iOS 誤差対策で +1）
			const isFullyVisible = navBody.scrollHeight <= availableHeight + 1;

			// スクロール不要なら全面禁止
			if (isFullyVisible) {
				e.preventDefault();
				return;
			}

			// スクロール可能でも、メニュー外のタッチはスクロール禁止
			if (!e.target.closest('[data-menu-body]')) {
				e.preventDefault();
			}
		},
		{ passive: false } // preventDefault を有効にする
	);

	// ============================================
	// スマホメニュー
	// ============================================
	const toggleMenu = () => {
		// ボタンをクリックでメニューを開閉
		document.querySelectorAll(`[${dataToggleMenu}-trigger]`).forEach((triggerElement) => {
			triggerElement.addEventListener('click', () => {
				const isOpen = bodyClassList.contains(menuOpenClass) && bodyClassList.contains(menuShowClass);
				//メニューが既に開いている場合
				if (isOpen) {
					//スクロール防止を解除
					isMenuOpen = false;
					toggleMenuClass(menuShowClass, menuOpenClass);
				} else {
					//スクロール防止
					isMenuOpen = true;
					toggleMenuClass(menuOpenClass, menuShowClass);
				}
			});
		});
	};

	// ============================================
	// スムーススクロール
	// ============================================
	const smoothScroll = () => {
		// rem → px 変換用関数
		const getRem = (value) => value * parseFloat(getComputedStyle(document.documentElement).fontSize);

		// html要素（data属性取得用）
		const root = document.documentElement;

		// data属性からSP用ヘッダーオフセットを取得
		let dataHeaderOffsetSp = null;
		// data-headeroffset-sp が設定されているか確認
		if (root.dataset.headeroffsetSp !== undefined) {
			// 数値に変換
			dataHeaderOffsetSp = Number(root.dataset.headeroffsetSp);
		}

		// data属性からPC用ヘッダーオフセットを取得
		let dataHeaderOffsetPc = null;
		// data-headeroffset-pc が設定されているか確認
		if (root.dataset.headeroffsetPc !== undefined) {
			// 数値に変換
			dataHeaderOffsetPc = Number(root.dataset.headeroffsetPc);
		}
		// ハッシュリンク要素を取得
		const hashLinks = document.querySelectorAll("a[href^='#']");

		// ハッシュリンクが存在しない場合は処理終了
		if (!hashLinks.length) return;

		// 初期ヘッダーオフセット（SP想定）
		// data属性があればそちらを優先
		let headerOffset = dataHeaderOffsetSp ?? getRem(9);

		// ヘッダー高さをデバイスごとに更新
		const updateHeaderOffset = (e) => {
			// PC / SP 判定
			const isPc = e ? e.matches : mql.matches;

			if (isPc) {
				// PC時：data-headeroffset-pc があればそれを使用
				// なければデフォルト（9rem）
				headerOffset = dataHeaderOffsetPc !== null ? dataHeaderOffsetPc : getRem(10);
			} else {
				// SP時：data-headeroffset-sp があればそれを使用
				// なければデフォルト（4rem）
				headerOffset = dataHeaderOffsetSp !== null ? dataHeaderOffsetSp : getRem(6);
			}
		};

		// 指定したターゲットへスクロール
		const scrollToTarget = (selector) => {
			// selectorがあれば対象IDを取得
			const targetElement = selector ? document.getElementById(selector.replace('#', '')) : document.documentElement;

			// 対象要素が存在しない場合は処理しない
			if (!targetElement) return;

			// 対象要素ごとのヘッダーオフセットを取得
			let targetOffset = headerOffset; // 基本は現在のヘッダーオフセット

			// PC / SP 判定
			const isPc = mql.matches;
			if (isPc) {
				// PC用 data-headeroffset-pc があれば上書き
				if (targetElement.dataset.headeroffsetPc !== undefined) {
					targetOffset = Number(targetElement.dataset.headeroffsetPc);
				}
			} else {
				// SP用 data-headeroffset-sp があれば上書き
				if (targetElement.dataset.headeroffsetSp !== undefined) {
					targetOffset = Number(targetElement.dataset.headeroffsetSp);
				}
			}

			// スクロール位置を計算
			const targetTop = targetElement.getBoundingClientRect().top + window.pageYOffset - targetOffset;

			// スムーススクロール実行
			window.scrollTo({
				top: targetTop,
				behavior: 'smooth',
			});
		};

		// ハッシュリンククリック時の処理
		hashLinks.forEach((hashLink) => {
			hashLink.addEventListener('click', (e) => {
				// デフォルトのアンカー遷移を無効化
				e.preventDefault();

				// href属性を取得
				const href = hashLink.getAttribute('href');

				// 遷移先IDの判定
				const targetId = href === '#' || href === '' ? null : href;

				// スクロール実行
				scrollToTarget(targetId);

				// メニューが開いている場合は閉じる
				if (bodyClassList.contains(menuOpenClass) && bodyClassList.contains(menuShowClass)) {
					isMenuOpen = false;
					toggleMenuClass(menuShowClass, menuOpenClass);
				}
			});
		});

		// Safari対策（初期ハッシュ位置ズレ防止）
		// const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

		// if (isSafari && window.location.hash) {
		// 	window.scrollTo(0, 1);
		// 	setTimeout(() => {
		// 		scrollToTarget(window.location.hash);
		// 	}, 600);
		// }

		// ============================================
		// 外部リンク（URLハッシュ）からのアクセス対応（Safari対策込み）
		// ============================================

		// ブラウザの自動スクロールを無効化
		if ('scrollRestoration' in history) {
			history.scrollRestoration = 'manual';
		}

		if (window.location.hash) {
			window.addEventListener('load', () => {
				// 即時実行
				window.scrollTo(0, 0);

				// safari対策 次フレームでも実行
				requestAnimationFrame(() => {
					window.scrollTo(0, 0);
				});

				setTimeout(() => {
					scrollToTarget(window.location.hash);
				}, 100);
			});
		}

		// 初期化 & メディアクエリ監視
		updateHeaderOffset();
		mql.addEventListener('change', updateHeaderOffset);
	};

	// ============================================
	// モーダル
	// ============================================
	const modal = () => {
		// 共通変数
		const commonName = 'data-modal';
		const modalTarget = `${commonName}-target`;
		const modalTrigger = `${commonName}-trigger`;
		const modalCloseTrigger = `${commonName}-close`;
		const modalContent = `${commonName}-content`;
		//modal・アニメーション
		const modalActive = `${commonName}-active`;
		const modalAnimation = `${commonName}-animation`;
		const modalAnimationTrigger = `${commonName}-animationTrigger`;
		//クラス名
		const openClass = 'is-modal-open';
		const bodyFixedClass = 'is-contents-fixed';
		// ----------------------------
		// 共通関数
		// ----------------------------
		//モーダル共通処理
		const modalVisible = function (targetElement) {
			if (targetElement.getAttribute(modalAnimation)) {
				// CSSアニメーションを実行する要素を取得
				const wrap = targetElement.querySelector(`[${modalAnimationTrigger}]`);
				//アニメーションを実行
				if (wrap) {
					wrap.addEventListener('animationend', function handleClose() {
						targetElement.classList.toggle(openClass); // 表示用クラスを削除
						wrap.removeEventListener('animationend', handleClose); // 一度だけ実行になるようイベントリスナーを削除
					});
				} else {
					targetElement.classList.toggle(openClass); // 表示用クラスを削除
				}
			} else {
				//アニメーションがない場合はそのまま実行
				targetElement.classList.toggle(openClass); // 表示用クラスを削除
			}
		};

		//背景固定処理
		function handleTouchMove(event) {
			//クリック要素がモーダルの子要素だった場合はスルー
			if (event.target.getAttribute(modalContent) || event.target.closest(`[${modalContent}]`)) return;
			//モーダル外ならスクロール防止
			event.preventDefault();
		}

		//モーダルを開く
		const openModal = function (triggerElement) {
			if (!triggerElement) return; //各要素がなければスルー
			//既に他のモーダルが開いている場合
			const currentModal = document.querySelector(`.${openClass}`);
			if (currentModal) {
				currentModal.setAttribute(modalActive, 'false'); // アクティブ属性をfalseに更新
				// モーダルの処理
				modalVisible(currentModal);
			}

			//クリックしたトリガーとdata属性の値が一致したターゲットを取得
			const triggerValue = triggerElement.getAttribute(modalTrigger);
			const target = body.querySelector(`[${modalTarget}="${triggerValue}"]`);
			if (!target) return; //要素がなければスルー

			target.setAttribute(modalActive, 'true'); // アクティブ属性をtrueに更新
			target.classList.toggle(openClass); // 表示用クラスを削除
			bodyClassList.add(bodyFixedClass); // 背景固定も解除

			//スクロール防止
			document.addEventListener('touchmove', handleTouchMove, {
				passive: false,
			});
		};

		//モーダルを閉じる
		const closeModal = function (triggerElement) {
			if (!triggerElement) return; //各要素がなければスルー

			//対象のモーダル要素を取得
			const target = triggerElement.closest(`[${modalTarget}]`);

			if (!target) return; //一致するコンテンツがなければスルー
			if (triggerElement.getAttribute(modalCloseTrigger) && triggerElement.closest(`[${modalContent}]`)) return; //クリック要素がモーダルの子要素だった場合はスルー

			// アクティブ属性をfalseに更新
			target.setAttribute(modalActive, 'false');
			modalVisible(target); // モーダルの処理
			bodyClassList.remove(bodyFixedClass); // 背景固定も解除

			//スクロール防止を解除
			document.removeEventListener('touchmove', handleTouchMove);
		};

		// ----------------------------
		// 処理
		// ----------------------------
		const triggers = document.querySelectorAll(`[${modalTrigger}]`);
		if (!triggers.length) return; //モーダル要素がなければスルー

		document.addEventListener('click', function (event) {
			//モーダルを開く処理
			if (event.target.getAttribute(modalTrigger)) {
				openModal(event.target);
			}

			//モーダルを閉じる処理
			//data属性に値がないと一律でfalseになるので!== null必須
			if (event.target.getAttribute(modalCloseTrigger) !== null) {
				closeModal(event.target);
			}
		});
	};

	// ============================================
	// ナビゲーションテキストの色変え
	// ============================================
	const txtColorChange = () => {

		// ヘッダー取得（ScrollTriggerの開始位置計算用）
		const header = document.querySelector('.l-header');

		// 色切り替え対象のセクションを取得
		const sections = document.querySelectorAll('[data-color-change]');

		// 対象がなければ終了
		if (!sections.length) return;

		// グループごとの状態を管理
		const groups = {};

		// 色更新
		const update = (state) => {

			const current = state.activeSections.at(-1);

			state.targets.forEach((target) => {

				// 色を削除
				target.classList.remove(
					'is-white',
					'is-black'
				);

				if (!current) return;

				target.classList.add(`is-${current.color}`);

			});

		};

		sections.forEach((section) => {

			// data-color-group が指定されていない場合は"default" グループとして扱う
			const targetGroups = (section.dataset.colorGroup || 'default')
				.split(',')
				.map(item => {

					const values = item.split(':').map(v => v.trim());

					let group = values[0];
					let device = 'all';
					let color = section.dataset.colorChange || 'white';

					// 2つ指定の場合
					if (values.length === 2) {

						// sp / pcならデバイス
						if (values[1] === 'sp' || values[1] === 'pc') {
							device = values[1];
						}
						// white / blackなら色
						else {
							color = values[1];
						}

					}

					// 3つ指定の場合
					if (values.length === 3) {
						device = values[1];
						color = values[2];
					}

					return {
						group,
						device,
						color
					};

				});

			targetGroups.forEach(({ group, device, color }) => {

				const isSp = bodyClassList.contains(spWidthClass);

				// グループ管理用キー
				const groupKey = device === 'all'
					? group
					: `${group}:${device}`;

				// デバイス判定
				if (
					(device === 'sp' && !isSp) ||
					(device === 'pc' && isSp)
				) {
					return;
				}

				// グループが未登録なら初期化
				if (!groups[groupKey]) {

					groups[groupKey] = {

						// このグループに属する変更対象
						targets: document.querySelectorAll(
							`[data-color-target="${group}"]`
						),

						// 現在有効なセクション
						activeSections: []

					};

				}
				const state = groups[groupKey];

				// 管理配列からセクションを削除
				const remove = () => {

					const index = state.activeSections.findIndex(
						(item) => item.section === section
					);

					if (index !== -1) {
						state.activeSections.splice(index, 1);
					}

				};

				ScrollTrigger.create({

					trigger: section,

					start: () => `top ${header.offsetHeight}px`,
					end: () => `bottom ${header.offsetHeight}px`,

					onEnter: () => {

						state.activeSections.push({
							section,
							color
						});
						update(state);
					},

					onLeave: () => {
						remove();
						update(state);
					},

					onEnterBack: () => {
						state.activeSections.push({
							section,
							color
						});
						update(state);
					},

					onLeaveBack: () => {
						remove();
						update(state);
					}

				});

			});
		});
	};

	/* ==========================================================
		ナビゲーションテキストの色変え・使用例
	========================================================== */

	/*
		<!-- 従来 -->
		data-color-group="header"
		data-color-change="white"

		<!-- 色だけ指定 -->
		data-color-group="header:white"

		<!-- PCだけ -->
		data-color-group="header:pc"

		<!-- PCだけ白 -->
		data-color-group="header:pc:white"

		<!-- SPだけ黒 -->
		data-color-group="logo:sp:black"

		<!-- 混在 -->
		data-color-group="header:white,logo:sp:black"
	*/


	// ============================================
	// 実行
	// ============================================
	addClassWindow();
	addClassScroll();
	toggleMenu();
	smoothScroll();
	modal();
	txtColorChange();

	// 外部ファイル実行
	initScrambleAnimation();

	// PC/SP切り替え時に再生成（768pxの境界を跨いだ時のみ実行）
	mql.addEventListener('change', () => {

		// txtColorChange()内で作成したScrollTriggerのみ削除
		ScrollTrigger.getAll().forEach((trigger) => {
			trigger.kill();
		});

		// 再生成
		txtColorChange();

	});

})();
